import React from 'react';
import Cookies from 'universal-cookie';
import { Navbar, Footer } from '@/components';
import {
  GlobalSettingsInterface,
  LanguageCodeType,
  LinkInterface,
  SiteBannerInterface,
} from '@/types';
import { Toaster } from '@/components/ui/toaster';

interface Language {
  language: LanguageCodeType;
  href: string;
}

interface Props {
  children: React.ReactNode;
  languageVersions: Language[];
  currentLanguage: string;
  languages: LanguageCodeType[];
  globalSettings: GlobalSettingsInterface[];
  siteBanner: SiteBannerInterface[];
}

const Layout: React.FC<Props> = ({
  children,
  languageVersions,
  currentLanguage,
  languages,
  globalSettings,
  siteBanner,
}) => {
  // Fetch preferred language from cookies
  const cookies = new Cookies();
  const preferredLanguage = cookies.get('preferredLanguage') || 'en';

  // Filter the data based on the preferred language
  const filteredData = globalSettings.find((edge) => edge.node.language === preferredLanguage);
  const siteBannerData = siteBanner.find((edge) => edge.node.language === preferredLanguage);

  // Handle cases where data for the preferred language is not available
  if (!filteredData) {
    // Fallback to a default language or handle the error appropriately
    console.error('No data found for the preferred language:', preferredLanguage);
  }

  const languageNotAvailableTitle =
    filteredData?.node?.languageNotAvailableTitle || 'Language not available';
  const languageNotAvailableBlurb = filteredData?.node?.languageNotAvailableBlurb || '';
  const primaryNavigation = filteredData?.node?.primaryNavigation || [];
  const footerNavigation = filteredData?.node?.footerNavigation || [];
  const copyrightText =
    filteredData?.node?.copyrightText || "© 2024 National Children's Museum. All Rights Reserved.";

  const bannerEnabled = siteBannerData?.node?.enabled || false;
  const bannerTag = siteBannerData?.node?.tag || '';
  const bannerText = siteBannerData?.node?.text || '';
  const includeBannerCta = siteBannerData?.node?.includeCta || false;
  const bannerCta = siteBannerData?.node?.cta || null;
  const bannerStartDateTime = siteBannerData?.node?.startDateTime || undefined;
  const bannerEndDateTime = siteBannerData?.node?.endDateTime || undefined;

  const formatLinkInterface = (menuItem: any): LinkInterface => ({
    label: menuItem.title,
    internalLink: menuItem?.target?.internalLink
      ? {
          slug: { current: menuItem?.target?.internalLink?.slug?.current },
          pageParent: menuItem?.target?.internalLink?.pageParent
            ? {
                slug: { current: menuItem?.target?.internalLink?.pageParent?.slug?.current },
                pageParent: menuItem?.target?.internalLink?.pageParent?.pageParent
                  ? {
                      slug: {
                        current:
                          menuItem?.target?.internalLink?.pageParent?.pageParent?.slug?.current,
                      },
                    }
                  : undefined,
              }
            : undefined,
          language: menuItem?.target?.internalLink?.language,
          _type: menuItem?.target?.internalLink?._type,
        }
      : undefined,
    externalLink: menuItem?.target?.externalUrl,
  });

  const PrimaryNavigationItem = primaryNavigation.map(({ menu, Submenu }) => ({
    ...formatLinkInterface(menu),
    child: Submenu?.map(formatLinkInterface),
  }));

  const FooterNavigationItem = footerNavigation.map(({ title, target }) => ({
    ...formatLinkInterface({ title, target }),
  }));

  return (
    <>
      <a
        href='#content'
        className='absolute -top-12 left-2 z-[110] rounded-full bg-yellow-500 px-4 py-2.5 text-base font-semibold text-black transition-all focus:top-2 focus:outline-1 focus:outline-offset-2'
      >
        Skip to main content
      </a>

      <svg className='clippy'>
        <defs>
          <clipPath
            id='clip-blob'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M1 0.166826C1 0.0746993 0.926525 0 0.835908 0H0.164092C0.0734748 0 0 0.0746993 0 0.166826C0 0.254618 0.0667254 0.326595 0.151428 0.333174C0.0667254 0.339753 0 0.411707 0 0.499521C0 0.591648 0.0734748 0.666347 0.164092 0.666347C0.0734748 0.666347 0 0.741046 0 0.833173C0 0.9253 0.0734748 1 0.164092 1H0.835908C0.926525 1 1 0.9253 1 0.833173C1 0.741046 0.926525 0.666347 0.835908 0.666347C0.926525 0.666347 1 0.591648 1 0.499521C1 0.411729 0.933275 0.339753 0.848572 0.333174C0.933275 0.326595 1 0.25464 1 0.166826Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-blob-wide'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M1 0.166828C1 0.0746958 0.963265 0 0.917955 0H0.0820448C0.0367347 0 0 0.0746958 0 0.166828C0 0.254629 0.0333582 0.32659 0.0757108 0.333172C0.0333582 0.339753 0 0.411715 0 0.499515C0 0.591659 0.0367347 0.666343 0.0820448 0.666343C0.0367347 0.666343 0 0.741039 0 0.833172C0 0.925316 0.0367347 1 0.0820448 1H0.917949C0.96326 1 0.999994 0.925304 0.999994 0.833172C0.999994 0.741027 0.96326 0.666343 0.917949 0.666343C0.96326 0.666343 0.999994 0.591647 0.999994 0.499515C0.999994 0.411715 0.966636 0.339753 0.924283 0.333172C0.966636 0.32659 0.999994 0.254629 0.999994 0.166828H1Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-half-circle-down'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M0.886494 0C0.961806 0 1.01647 0.139921 0.995409 0.28C0.932915 0.695715 0.734739 0.999484 0.499982 0.999484C0.265224 0.999484 0.0670485 0.695715 0.00455428 0.28C-0.0165081 0.139921 0.0381571 0 0.113469 0H0.886517H0.886494Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-half-circle-up'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M0.113506 0.999512C0.038194 0.999512 -0.01647 0.859591 0.00459099 0.719512C0.067085 0.303797 0.265261 2.77162e-05 0.500018 2.77162e-05C0.734776 2.77162e-05 0.932951 0.303797 0.995446 0.719512C1.01651 0.859591 0.961843 0.999512 0.886531 0.999512H0.113506Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-oval-wide'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M0.498654 0H0.299734C0.150309 0 0 0.206215 0 0.5C0 0.858757 0.199823 1 0.299734 1H0.498654H0.702918C0.846154 1 1 0.798023 1 0.5C1 0.201977 0.83466 0 0.702918 0H0.498654Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-circle'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M0.5 1C0.776142 1 1 0.776142 1 0.5C1 0.223858 0.776142 0 0.5 0C0.223858 0 0 0.223858 0 0.5C0 0.776142 0.223858 1 0.5 1Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-square'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M0.898467 0H0.101533C0.045458 0 0 0.045458 0 0.101533V0.898467C0 0.954542 0.045458 1 0.101533 1H0.898467C0.954542 1 1 0.954542 1 0.898467V0.101533C1 0.045458 0.954542 0 0.898467 0Z'
              fill='white'
            />
          </clipPath>
          <clipPath
            id='clip-shield'
            clipPathUnits='objectBoundingBox'
          >
            <path
              d='M0.886494 0C0.961806 0 1.01647 0.139921 0.995409 0.28C0.932915 0.695715 0.734739 0.999484 0.499982 0.999484C0.265224 0.999484 0.0670485 0.695715 0.00455428 0.28C-0.0165081 0.139921 0.0381571 0 0.113469 0H0.886517H0.886494Z'
              fill='white'
            />
          </clipPath>
        </defs>
         
      </svg>

      <Navbar
        items={PrimaryNavigationItem}
        languageVersions={languageVersions}
        languages={languages}
        currentLanguage={currentLanguage}
        languageNotAvailableTitle={languageNotAvailableTitle}
        languageNotAvailableBlurb={languageNotAvailableBlurb}
        bannerEnabled={bannerEnabled}
        bannerTag={bannerTag}
        bannerText={bannerText}
        includeBannerCta={includeBannerCta}
        bannerCta={bannerCta}
        bannerStartDateTime={bannerStartDateTime}
        bannerEndDateTime={bannerEndDateTime}
      />
      <main id='content'>{children}</main>
      <Footer
        navigationLinks={FooterNavigationItem}
        copyrightText={copyrightText}
        businessHours={filteredData?.node?.businessHours}
        locationInfo={filteredData?.node?.locationInfo}
        newsletterUrl={filteredData?.node?.newsletterUrl || '#'}
        instagramLink={filteredData?.node?.instagramLink}
        facebookLink={filteredData?.node?.facebookLink}
        twitterLink={filteredData?.node?.twitterLink}
        youtubeLink={filteredData?.node?.youtubeLink}
      />
      <Toaster />
    </>
  );
};

export default Layout;
